import { goSolidApi } from "@/composables/useGoSolidApi";
import { base } from "./cnmcApi";
import type {
  CnmcHoldingsResponse,
  OutboundFiling,
  OutboundFilingRequest,
  OutboundPreviewRequest,
  OutboundPreviewResponse,
} from "./cnmcTypes";

/**
 * Transfers and exports prepared from synced CNMC holdings. Every call but `holdings` is
 * connection-scoped; failed requests reject, and the caller hands them to its error handler. Receipt
 * entry, replacement, checks, downloads and staff resolution go through `cnmcApi`'s plan-agnostic
 * application routes.
 */
export const cnmcOutboundApi = {
  /**
   * What Send needs to file each live CNMC holding row of every CNMC connection of the company, keyed
   * by the row's portfolio item. Committed (pending) and empty rows are not listed.
   */
  holdings: (companyId: number) =>
    goSolidApi.get<CnmcHoldingsResponse>(
      `/issuance-reporting/companies/${companyId}/cnmc/holdings`
    ),
  /** Groups a selection into form rows and reports what blocks it. Writes nothing. */
  preview: (
    companyId: number,
    connectionId: number,
    data: OutboundPreviewRequest
  ) =>
    goSolidApi.post<OutboundPreviewResponse>(
      `${base(companyId, connectionId)}/outbound-previews`,
      data
    ),
  /**
   * Creates the order, the application and its ZIP. A 409 with `error_type`
   * CNMC_OUTBOUND_AVAILABILITY_CHANGED means the selection must be previewed again.
   */
  create: (
    companyId: number,
    connectionId: number,
    data: OutboundFilingRequest
  ) =>
    goSolidApi.post<OutboundFiling>(
      `${base(companyId, connectionId)}/outbound-filings`,
      data
    ),
  /** Discards an unsubmitted file, or closes one CNMC rejected or withdrew; answers with the filing. */
  abandon: (
    companyId: number,
    connectionId: number,
    applicationId: number,
    reason?: string | null
  ) =>
    goSolidApi.post<OutboundFiling>(
      `${base(companyId, connectionId)}/outbound-filings/${applicationId}/abandonment`,
      { reason: reason || null }
    ),
};
